import {ScreenLayout} from "@/components/layouts/page/ScreenLayout";
import TableLayout from "@/components/layouts/page/TableLayout";
import {
  getVideos,
  setPage,
  setSearch,
  setSelectedData,
  setSize,
  setSort,
  setTotalPages,
} from "@/redux/slices/videoSlices/tableSlice";

import {useCallback, useEffect, useMemo} from "react";

import {ControlButton} from "@ui/buttons/ControlButton";
import {TableButton} from "@ui/buttons/TableButton";
import {RootCheckbox} from "@ui/inputs/RootCheckbox";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

export const VideoTablePage = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {demo} = useSelector((state) => state.common.candidate);
  const {page, size, sort, totalPages, status, search} = useSelector(
    (state) => state.video.table,
  );

  const servier_test_server = window.location.host.includes("servier");
  const sanofi_test_server = window.location.host.includes("sanofi");
  // const localhost = window.location.host.includes('localhost')

  const handleClickDeactivateAll = () => {
    console.log("Deactivated check videos!");
  };

  const handleCheckFilter = () => {
    console.log("Show inactive videos!");
  };

  const handleSearch = useCallback(
    (value) => {
      clearTimeout(window.searchTimeout);

      window.searchTimeout = setTimeout(() => {
        dispatch(setSearch(value));
        dispatch(getVideos({page: 1, size, search: value}));
      }, 500);
    },
    [dispatch, size],
  );

  const goToPage = (pageIndex) => {
    dispatch(setPage(pageIndex + 1));
  };

  const handleSelectSize = (option) => {
    dispatch(setSize(+option));
    dispatch(setPage(1));
    dispatch(setTotalPages(Math.ceil(totalPages / size)));
  };

  const handleSelectSort = (option) => {
    dispatch(setSort(option));
  };

  const controls = [
    {
      id: 0,
      button: (
        <ControlButton
          color={"var(--green)"}
          txt={t("button.add")}
          link
        />
      ),
    },
    {
      id: 1,
      button: (
        <ControlButton
          color={"var(--red)"}
          txt={t("button.deactivate")}
          onClick={() => handleClickDeactivateAll()}
        />
      ),
    },
  ];

  const data = useMemo(() => {
    if (servier_test_server) {
      return [
        {
          id: 0,
          name: t("video_servier_1.name"),
          product: t("video_servier_1.product"),
          line: t("video_servier_1.line"),
          created_at: "2025-05-28",
          inactive: false,
        },
        {
          id: 1,
          name: t("video_servier_2.name"),
          product: t("video_servier_2.product"),
          line: t("video_servier_2.line"),
          created_at: "2025-05-28",
          inactive: false,
        },
      ];
    }

    if (sanofi_test_server) {
      return [
        {
          id: 0,
          name: t("video_sanofi_1.name"),
          product: t("video_sanofi_1.product"),
          line: t("video_sanofi_1.line"),
          created_at: "2025-05-28",
          inactive: false,
        },
      ];
    }

    return [
      {
        id: 0,
        name: t("video_1.name"),
        product: t("video_1.product"),
        line: t("video_1.line"),
        created_at: "2024-10-21",
        inactive: false,
      },
      {
        id: 1,
        name: t("video_2.name"),
        product: t("video_2.product"),
        line: t("video_2.line"),
        created_at: "2024-10-21",
        inactive: false,
      },
      {
        id: 2,
        name: t("video_3.name"),
        product: t("video_3.product"),
        line: t("video_3.line"),
        created_at: "2024-10-21",
        inactive: false,
      },
      {
        id: 3,
        name: t("video_4.name"),
        product: t("video_4.product"),
        line: t("video_4.line"),
        created_at: "2024-10-21",
        inactive: false,
      },
    ];
  }, [servier_test_server, sanofi_test_server, t]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: t("table.name"),
        accessor: "name",
        Cell: ({cell: {value}}) => (
          <span dangerouslySetInnerHTML={{__html: value}} />
        ),
        width: 400,
      },
      {
        Header: t("table.product"),
        accessor: "product",
        Cell: ({cell: {value}}) => (
          <span dangerouslySetInnerHTML={{__html: value}} />
        ),
        width: 260,
      },
      {
        Header: t("table.product_line"),
        accessor: "line",
        Cell: ({cell: {value}}) => (
          <span dangerouslySetInnerHTML={{__html: value}} />
        ),
        width: 320,
      },
      {
        Header: t("table.created"),
        accessor: "created_at",
        Cell: ({cell: {value}}) =>
          value ? moment(value).format("DD-MM-YYYY") : "",
        width: 225,
      },
      {
        Header: t("table.actions"),
        Cell: ({row}) => (
          <div
            style={
              demo
                ? {
                    justifyContent: "flex-end",
                    marginLeft: "auto",
                    width: "fit-content",
                  }
                : {}
            }
            className={"flex gap-[8rem] phone:gap-[40rem]"}
          >
            <TableButton
              icon={"view"}
              color={"var(--orange)"}
              label={t("table.button.view")}
              action={"view"}
              item={row.original}
            />

            {!demo && (
              <>
                <TableButton
                  icon={"edit"}
                  color={"var(--blueLight)"}
                  label={t("table.button.edit")}
                  action={"edit"}
                  item={row.original}
                />

                <TableButton
                  icon={"deactivate"}
                  color={"var(--red)"}
                  label={t("table.button.deactivate")}
                  action={"deactivate"}
                  item={row.original}
                />
              </>
            )}
          </div>
        ),
        width: 210,
      },
    ];

    if (!demo) {
      baseColumns.unshift({
        id: "selection",
        Header: ({getToggleAllRowsSelectedProps}) => (
          <div>
            <RootCheckbox {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({row}) => (
          <div>
            <RootCheckbox {...row.getToggleRowSelectedProps()} />
          </div>
        ),
        width: 50,
      });
    }

    return baseColumns;
  }, [demo]);

  useEffect(() => {
    if (Object.keys(sort).length !== 0) {
      dispatch(getVideos({page, size}));
    }
  }, [page, size, sort]);

  useEffect(() => {
    const firstAccessor = columns.find((column) => column.accessor);

    dispatch(
      setSort({name: firstAccessor.Header, value: firstAccessor.accessor}),
    );
  }, []);

  return (
    // тестовая data
    // <ScreenLayout controlsList={!demo && controls} title={'Training Library'}>
    // 	<TableLayout
    // 		columns={columns}
    // 		data={data}
    // 		filterCheckbox={
    // 			!demo && (
    // 				<RootCheckbox
    // 					onChange={() => alert('filter-table')}
    // 					label={'Show Inactive Videos'}
    // 				/>
    // 			)
    // 		}
    // 	/>
    // </ScreenLayout>

    <ScreenLayout
      controlsList={!demo && controls}
      title={t("page.library.title")}
    >
      <TableLayout
        serverStatus={status}
        columns={columns}
        data={data} // videos
        dataSort={sort}
        selectSort={handleSelectSort}
        dataSize={size}
        selectSize={handleSelectSize}
        searchPlaceholder={t("table.search_videos")}
        searchValue={search}
        onSearch={handleSearch}
        totalPages={totalPages}
        currentPage={page}
        toPage={goToPage}
        setSelected={(items) => dispatch(setSelectedData(items))}
        filterCheckbox={
          !demo && (
            <RootCheckbox
              onChange={() => handleCheckFilter()}
              label={t("page.library.checkbox_filter")}
            />
          )
        }
      />
    </ScreenLayout>
  );
};
